import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockService } from '../../services/api';
import { Intrant, MouvementStock, LotIntrant, Fournisseur, PaginatedResponse } from '@/types';
import { X, Package, TrendingUp, TrendingDown } from 'lucide-react';

interface MouvementFormProps {
  intrant: Intrant;
  onClose: () => void;
  onSuccess?: () => void;
}

interface MouvementFormData {
  type_mouvement: 'entree' | 'sortie';
  motif: 'achat' | 'livraison' | 'consommation' | 'perte' | 'peremption' | 'ajustement';
  quantite: number;
  date_mouvement: string;
  reference_document?: string;
  notes?: string;
  // Pour les entrées
  numero_lot?: string;
  date_peremption?: string;
  prix_unitaire?: number;
  fournisseur?: number;
  // Pour les sorties
  lot?: number;
}

const MouvementForm: React.FC<MouvementFormProps> = ({ intrant, onClose, onSuccess }) => {
  const queryClient = useQueryClient();

  const { register, handleSubmit, formState: { errors }, watch } = useForm<MouvementFormData>({
    defaultValues: {
      date_mouvement: new Date().toISOString().slice(0, 16),
      type_mouvement: 'entree'
    }
  });

  const typeMouvement = watch('type_mouvement');

  // Récupération des fournisseurs
  const { data: fournisseursResponse } = useQuery<PaginatedResponse<Fournisseur>>({
    queryKey: ['fournisseurs'],
    queryFn: () => stockService.getFournisseurs().then(res => res.data)
  });
  const fournisseurs = fournisseursResponse?.results || [];

  // Récupération des lots pour cet intrant
  const { data: lotsResponse } = useQuery<PaginatedResponse<LotIntrant>>({
    queryKey: ['lots'],
    queryFn: () => stockService.getLots().then(res => res.data)
  });
  const lots = lotsResponse?.results || [];

  // Mutation pour créer un mouvement
  const mouvementMutation = useMutation<MouvementStock, Error, any>({
    mutationFn: (data) => stockService.createMouvement({
      ...data,
      intrant: intrant.id
    }).then(res => res.data),
    onSuccess: () => {
      // Le parent (StockList) s'occupe de rafraîchir toutes les données du dashboard
      onSuccess?.();
    }
  });

  const onSubmit: SubmitHandler<MouvementFormData> = async (data) => {
    try {
      await mouvementMutation.mutateAsync(data);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const getMotifsForType = (type: 'entree' | 'sortie'): Array<{ value: string; label: string }> => {
    if (type === 'entree') {
      return [
        { value: 'achat', label: 'Achat' },
        { value: 'livraison', label: 'Livraison' },
        { value: 'ajustement', label: 'Ajustement inventaire' }
      ];
    } else {
      return [
        { value: 'consommation', label: 'Consommation' },
        { value: 'perte', label: 'Perte' },
        { value: 'peremption', label: 'Péremption' },
        { value: 'ajustement', label: 'Ajustement inventaire' }
      ];
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Mouvement de stock - {intrant.nom}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          title="Fermer le formulaire"
          aria-label="Fermer le formulaire"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Informations sur l'intrant */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">{intrant.code} - {intrant.nom}</h3>
            <p className="text-sm text-gray-600">Stock actuel: {intrant.stock_actuel} {intrant.unite}</p>
          </div>
          <Package className="h-8 w-8 text-gray-400" />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de mouvement *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  {...register('type_mouvement', { required: 'Le type est requis' })}
                  value="entree"
                  className="mr-2 text-gray-900"
                />
                <TrendingUp className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-sm">Entrée</span>
              </label>
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  {...register('type_mouvement', { required: 'Le type est requis' })}
                  value="sortie"
                  className="mr-2 text-gray-900"
                />
                <TrendingDown className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-sm">Sortie</span>
              </label>
            </div>
            {errors.type_mouvement && <p className="text-red-500 text-sm mt-1">{errors.type_mouvement.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motif *
            </label>
            <select
              {...register('motif', { required: 'Le motif est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            >
              <option value="">Sélectionner un motif</option>
              {getMotifsForType(typeMouvement).map(motif => (
                <option key={motif.value} value={motif.value}>
                  {motif.label}
                </option>
              ))}
            </select>
            {errors.motif && <p className="text-red-500 text-sm mt-1">{errors.motif.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantité *
            </label>
            <input
              type="number"
              min="1"
              {...register('quantite', { required: 'La quantité est requise' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            />
            {errors.quantite && <p className="text-red-500 text-sm mt-1">{errors.quantite.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date et heure *
            </label>
            <input
              type="datetime-local"
              {...register('date_mouvement', { required: 'La date est requise' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            />
            {errors.date_mouvement && <p className="text-red-500 text-sm mt-1">{errors.date_mouvement.message}</p>}
          </div>
        </div>

        {/* Champs spécifiques aux entrées */}
        {typeMouvement === 'entree' && (
          <div className="border-2 border-green-200 rounded-lg p-6 bg-green-50">
            <h3 className="text-lg font-semibold text-green-800 mb-4">📦 Informations d'entrée</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fournisseur
                </label>
                <select
                  {...register('fournisseur')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                >
                  <option value="">Sélectionner un fournisseur</option>
                  {fournisseurs.map(fournisseur => (
                    <option key={fournisseur.id} value={fournisseur.id}>
                      {fournisseur.nom}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de lot
                </label>
                <input
                  {...register('numero_lot')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                  placeholder="Ex: LOT2024001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de péremption
                </label>
                <input
                  type="date"
                  {...register('date_peremption')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix unitaire
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  {...register('prix_unitaire')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                />
              </div>
            </div>
          </div>
        )}

        {/* Champs spécifiques aux sorties */}
        {typeMouvement === 'sortie' && lots.length > 0 && (
          <div className="border-2 border-red-200 rounded-lg p-6 bg-red-50">
            <h3 className="text-lg font-semibold text-red-800 mb-4">📤 Informations de sortie</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lot à utiliser (optionnel)
              </label>
              <select
                {...register('lot')}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              >
                <option value="">Sélectionner un lot</option>
                {lots.map(lot => (
                  <option key={lot.id} value={lot.id}>
                    {lot.numero_lot} - Restant: {lot.quantite_restante}
                    {lot.date_peremption && ` - Expire: ${new Date(lot.date_peremption).toLocaleDateString('fr-FR')}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Référence document
          </label>
          <input
            {...register('reference_document')}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Ex: Facture F2024-001, Bon de livraison..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            {...register('notes')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Notes complémentaires..."
          />
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={mouvementMutation.isLoading}
            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
          >
            {mouvementMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MouvementForm;